import { ref, computed, type ComputedRef } from 'vue'
import { fetchCmsMedia } from '@streampac.io/chef_sp_1'
import { useAuthenticationData } from './useAuthenticationData'
import { useCurrentUserData } from './useUserData'
import { useGlobalStore } from '@/stores/global'

export interface useCompanyLogoModule {
  companyLogo: ComputedRef<string | null>
  isLoadingLogo: ComputedRef<boolean>
  fetchCompanyLogo(): Promise<void>
  isLogoAvailable: ComputedRef<boolean>
}

// Global state to cache the logo across components
const globalCompanyLogo = ref<string | null>(null)
const globalIsLoadingLogo = ref(false)
const globalIsLogoFetched = ref(false)

export const useCompanyLogo: () => useCompanyLogoModule = () => {
  const isAuthenticated = computed(() => useAuthenticationData().data.value.isAuthenticated)
  const { getCurrentUser } = useCurrentUserData()
  const globalStore = useGlobalStore()

  // Function to fetch the company logo
  const fetchCompanyLogo = async (): Promise<void> => {
    // If already loading, don't start another fetch
    if (globalIsLoadingLogo.value) {
      return
    }

    // If already fetched and we have a logo, don't fetch again
    if (globalIsLogoFetched.value && globalCompanyLogo.value) {
      return
    }

    // console.log('Starting fetchCompanyLogo')

    // Check if we have the required data
    if (!getCurrentUser.value?.customerGroupIds?.length) {
      //   console.log('No customer group IDs found')
      globalCompanyLogo.value = null
      globalIsLogoFetched.value = true
      return
    }

    const correctId = getCurrentUser.value.customerGroupIds[0]
    // console.log('Customer group ID:', correctId)

    if (!correctId) {
      //   console.log('No valid customer group ID')
      globalCompanyLogo.value = null
      globalIsLogoFetched.value = true
      return
    }

    // Check if customer group lookup is available
    if (!globalStore?.getCustomerGroupLookup) {
      //   console.log('Customer group lookup not available yet')
      globalCompanyLogo.value = null
      globalIsLogoFetched.value = true
      return
    }

    const correctGroup = globalStore.getCustomerGroupLookup.find((item) => item.id === correctId)
    //console.log('Found customer group:', correctGroup)

    if (!correctGroup || !correctGroup?.overviewImage) {
      console.log('No customer group or overview image found')
      globalCompanyLogo.value = null
      globalIsLogoFetched.value = true
      return
    }

    //console.log('Fetching media for:', correctGroup.overviewImage)

    try {
      globalIsLoadingLogo.value = true
      const response = await fetchCmsMedia(isAuthenticated.value, correctGroup.overviewImage)
      //console.log('Media response:', response)
      globalCompanyLogo.value = response?.url || null
      //console.log('Company logo set to:', globalCompanyLogo.value)
    } catch (error) {
      //console.error('Error fetching company logo:', error)
      globalCompanyLogo.value = null
    } finally {
      globalIsLoadingLogo.value = false
      globalIsLogoFetched.value = true
    }
  }

  // Computed properties
  const companyLogo = computed(() => globalCompanyLogo.value)
  const isLoadingLogo = computed(() => globalIsLoadingLogo.value)
  const isLogoAvailable = computed(() => {
    return globalIsLogoFetched.value && globalCompanyLogo.value !== null
  })

  return {
    companyLogo,
    isLoadingLogo,
    fetchCompanyLogo,
    isLogoAvailable
  }
}
